import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { actions as cacheActions } from '@store/slice/cache-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY, LOCAL_STORAGE_USER_KEY } from '@config/auth';
import { useStudentMeQuery, useStudentKeyQuery } from '@hook/iyagigo/useApi';
import { getTodayDateKey } from '@util/common';
import { queryClient } from '@config/query-client';
import * as api from '@api/iyagigo/api-function';
import { EQUERY_KEY } from '@constant/iyagigo/query-key';
import { EApiCharacterType, EApiLanguageCode, EApiStoryType, TClozeType } from '@datatype/iyagigo/api-type';

import {
  INFINITE_SCROLL_PER_PAGE,
  IYAGIGO_INITIAL_TODAY_DATE_KEY,
  IYAGIGO_TODAY_INIT_DATA_MIN_TIME,
  IYAGIGO_TODAY_INIT_DATA_MAX_TIME,
} from '@constant/iyagigo/variable';

export const useCacheInitPage = () => {
  const dispatch = useTypedDispatch();
  const studyLanguague = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const location = useLocation();

  useEffect(() => {
    dispatch(cacheActions.initialize());
  }, [location.pathname, dispatch, studyLanguague]);
};

/**
 * @description 앱 진입 시 학습중 플래그(isStudying) 초기화
 *
 * workbookReducer/classroomReducer는 redux-persist 대상이라 isStudying이 localStorage에 남는다.
 * 학습 도중 앱이 강제 종료되면(홈버튼/스와이프 종료/OS 메모리 회수) `isStudying: true`가 그대로 rehydrate되어,
 * 재실행 후 홈 화면인데도 사이드탭에서 학습언어 변경/메뉴 이동 시 `학습을 종료할까요?`(workbook-quit) 모달이 뜬다.
 *
 * isStudying은 "지금 학습 화면에 있는가"라는 세션 한정 상태이므로 앱 진입 시 항상 false로 되돌린다.
 * (학습기록 등 나머지 persist 상태는 유지)
 */
export const useStudyingInit = () => {
  const dispatch = useTypedDispatch();

  useEffect(() => {
    dispatch(workbookActions.storeIsStudying(false));
    dispatch(classroomActions.storeIsStudying(false));
  }, [dispatch]);
};

export const useStudentKey = () => {
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const storageUserKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
  const isSignedIn = isAuthenticated && Boolean(storageUserKey);
  // const mutation = useCreateStudentMutation();
  const studentMeQuery = useStudentMeQuery({ enabled: isSignedIn });
  const apiStudentKey = studentMeQuery.data?.data?.key;
  const location = useLocation();
  const [storageStudentKey, setStorageStudentKey] = useState<string | null>(() =>
    localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY),
  );
  const needCheckStudentKey = !isSignedIn && Boolean(storageStudentKey);
  const studentKeyQuery = useStudentKeyQuery({ enabled: needCheckStudentKey });
  const isSignedupStudent = studentKeyQuery.data?.data?.signedup ?? false;

  // 비회원: 온보딩 완료 시 발급된 studentKey 사용
  useEffect(() => {
    (async () => {
      if (isAuthenticated) return;
      setStorageStudentKey(localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY));
    })();
  }, [isAuthenticated, location.pathname]);

  // 회원: /student/me 응답의 key를 localStorage 단일 source에 동기화한다.
  useEffect(() => {
    if (!isAuthenticated || !apiStudentKey) return;
    localStorage.setItem(LOCAL_STORAGE_STUDENT_KEY, apiStudentKey);
    setStorageStudentKey(apiStudentKey);
  }, [isAuthenticated, apiStudentKey, location.pathname]);

  // 로그아웃/탈퇴/인증 실패 경합으로 회원 studentKey만 localStorage에 남은 경우를 정리한다.
  // 비회원 studentKey는 signedup=false이므로 유지한다.
  useEffect(() => {
    if (!needCheckStudentKey || studentKeyQuery.isPending || studentKeyQuery.isFetching || !isSignedupStudent) return;
    localStorage.removeItem(LOCAL_STORAGE_STUDENT_KEY);
    setStorageStudentKey(null);
  }, [needCheckStudentKey, studentKeyQuery.isPending, studentKeyQuery.isFetching, isSignedupStudent]);

  const studentKey = isSignedIn && apiStudentKey ? apiStudentKey : storageStudentKey;

  return {
    studentKey: studentKey,
    studentKeySyncing:
      (isSignedIn && !studentMeQuery.isFetchedAfterMount) ||
      (needCheckStudentKey && (!studentKeyQuery.isFetchedAfterMount || isSignedupStudent)),
  };
};

/**
 * @description 오늘의 학습 데이터 초기화 (splash 표시 + 학습 데이터 prefetch)
 *
 * ## useWebVersionCheck와의 타임라인 (mismatch 발생 시)
 *
 * - t=0      : RootApp mount → useWebVersionCheck + useTodayDataInit 동시 시작
 *              splash 표시, prefetch 4개 시작 (병렬)
 * - t=200    : /api/v1/version 응답 → mismatch 감지
 *              localStorage.clear() (studentKey만 보존, INITIAL_TODAY_DATE_KEY는 사라짐)
 *              setTimeout(6000) 시작
 * - t=3000~  : prefetch + minDelay 끝 OR API hang 시 maxTimeout(5000ms)
 *              finally에서 localStorage.setItem(INITIAL_TODAY_DATE_KEY, "오늘")
 *              setIsInitialized(true) → splash 사라짐
 * - t=6200   : window.location.href → reload
 *              splash setItem이 reload보다 먼저 실행됐으므로 "오늘" 박혀있음
 * - reload 후: needInitialize=false → splash 안 뜸 ✅
 *
 * useWebVersionCheck의 6초 reload 대기는 splash가 INITIAL_TODAY_DATE_KEY를 저장할 시간을 보장하기 위한 것.
 *
 * ## 안전장치
 * - MIN_TIME(3000ms): 너무 빨리 끝나서 splash가 깜빡이는 것 방지
 * - MAX_TIME(5000ms): API hang 시 splash 무한 노출 방지 (Promise.race)
 */
export const useTodayDataInit = () => {
  const location = useLocation();
  const isHomeBeginPage = location.pathname === IYAGIGO_PATH.HOME_BEGIN_PATH;
  const todayDateKey = getTodayDateKey();
  const initTodayDate = localStorage.getItem(IYAGIGO_INITIAL_TODAY_DATE_KEY);
  const hasStudentKey = Boolean(localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY));
  const needInitialize = isHomeBeginPage && hasStudentKey && initTodayDate !== todayDateKey;
  const [isInitialized, setIsInitialized] = useState(!needInitialize);
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const { studentKey } = useStudentKey();

  useEffect(() => {
    if (!needInitialize) return;

    // API hang/네트워크 끊김/studentKey 미발급 등으로 splash가 무한히 떠있는 것을 방지하는 안전장치
    // early return보다 위에서 등록해야 studentKey가 안 와도 보장됨
    const maxTimeoutId = setTimeout(() => {
      localStorage.setItem(IYAGIGO_INITIAL_TODAY_DATE_KEY, todayDateKey);
      setIsInitialized(true);
    }, IYAGIGO_TODAY_INIT_DATA_MAX_TIME);
    const cleanup = () => clearTimeout(maxTimeoutId);

    if (!studentKey || !studyLanguage) return cleanup;

    setIsInitialized(false);

    const languageCode =
      studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;
    const staleTime = 60 * 1000;

    const vocabularyType: TClozeType = 'VOCABULARY';
    const grammarType: TClozeType = 'GRAMMAR';

    const initialization = Promise.allSettled([
      new Promise((resolve) => setTimeout(resolve, IYAGIGO_TODAY_INIT_DATA_MIN_TIME)),
      // 매일학습
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.TODAY_CLOZE_WORKBOOK, vocabularyType, languageCode, studentKey, todayDateKey],
        queryFn: () => api.getTodayClozeWorkbook({ clozeType: vocabularyType, languageCode, studentKey }),
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.TODAY_CLOZE_WORKBOOK, grammarType, languageCode, studentKey, todayDateKey],
        queryFn: () => api.getTodayClozeWorkbook({ clozeType: grammarType, languageCode, studentKey }),
        staleTime,
      }),
      // 학습지
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.TODAY_WORKSHEET_WORKBOOK, languageCode, studentKey, todayDateKey],
        queryFn: () => api.getTodayWorksheetWorkbook({ languageCode, studentKey }),
        staleTime,
      }),
      // 교실 — useExpressionsQuery/useStoriesQuery는 InfiniteQuery. perPage는 useApi의 INFINITE_SCROLL_PER_PAGE와 일치
      queryClient.prefetchInfiniteQuery({
        queryKey: [EQUERY_KEY.EXPRESSIONS, languageCode, todayDateKey],
        queryFn: () =>
          api.getExpressions({ page: 1, perPage: INFINITE_SCROLL_PER_PAGE, languageCode, releaseDate: todayDateKey }),
        initialPageParam: 1,
        staleTime,
      }),
      queryClient.prefetchInfiniteQuery({
        queryKey: [EQUERY_KEY.STORIES, languageCode, EApiStoryType.MINI_STORY, todayDateKey],
        queryFn: () =>
          api.getStories({
            page: 1,
            perPage: INFINITE_SCROLL_PER_PAGE,
            languageCode,
            storyType: EApiStoryType.MINI_STORY,
            releaseDate: todayDateKey,
          }),
        initialPageParam: 1,
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [
          EQUERY_KEY.CLASSROOM_CHARACTER_GROUP_WORKBOOKS,
          EApiLanguageCode.JAPANESE,
          EApiCharacterType.HIRAGANA,
          studentKey,
        ],
        queryFn: () =>
          api.getCharacterGroupWorkbooks({
            languageCode: EApiLanguageCode.JAPANESE,
            characterType: EApiCharacterType.HIRAGANA,
            studentKey,
          }),
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [
          EQUERY_KEY.CLASSROOM_CHARACTER_GROUP_WORKBOOKS,
          EApiLanguageCode.JAPANESE,
          EApiCharacterType.KATAKANA,
          studentKey,
        ],
        queryFn: () =>
          api.getCharacterGroupWorkbooks({
            languageCode: EApiLanguageCode.JAPANESE,
            characterType: EApiCharacterType.KATAKANA,
            studentKey,
          }),
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.CLASSROOM_GRAMMAR_WORKBOOKS, languageCode, studentKey],
        queryFn: () =>
          api.getClassroomGrammarWorkbooks({
            languageCode,
            studentKey,
          }),
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.CLASSROOM_EXPRESSION_WORKBOOKS, EApiLanguageCode.JAPANESE, studentKey],
        queryFn: () =>
          api.getClassroomExpressionWorkbooks({
            languageCode: EApiLanguageCode.JAPANESE,
            studentKey,
          }),
        staleTime,
      }),
      queryClient.prefetchQuery({
        queryKey: [EQUERY_KEY.CLASSROOM_CONVERSATION_WORKBOOKS, EApiLanguageCode.JAPANESE, studentKey],
        queryFn: () =>
          api.getClassroomConversationWorkbooks({
            languageCode: EApiLanguageCode.JAPANESE,
            studentKey,
          }),
        staleTime,
      }),
    ]);

    initialization.finally(() => {
      cleanup();
      localStorage.setItem(IYAGIGO_INITIAL_TODAY_DATE_KEY, todayDateKey);
      setIsInitialized(true);
    });

    return cleanup;
  }, [isAuthenticated, studyLanguage, studentKey, needInitialize, todayDateKey]);

  return isInitialized;
};

export const useMidnightPage = () => {
  const isDev = process.env.NODE_ENV === 'development';
  const navigate = useNavigate();
  const TEST_INTERVAL_MINUTES = 60;
  const getMinutesOfDay = () => new Date().getHours() * 60 + new Date().getMinutes();
  const mountTestRef = useRef<number>(Math.floor(getMinutesOfDay() / TEST_INTERVAL_MINUTES));
  const mountDateRef = useRef<string>(getTodayDateKey());

  useEffect(() => {
    const checkDateChange = () => {
      if (isDev) {
        const currentInterval = Math.floor(getMinutesOfDay() / TEST_INTERVAL_MINUTES);
        if (currentInterval !== mountTestRef.current) {
          navigate(IYAGIGO_PATH.BRIDGE_PATH, { replace: true });
          return;
        }
      }
      const currentDate = getTodayDateKey();
      if (currentDate !== mountDateRef.current) {
        navigate(IYAGIGO_PATH.BRIDGE_PATH, { replace: true });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDateChange();
      }
    };

    // 이벤트별 발화 시나리오 매핑
    // | 사용자 행동                          | setInterval | visibilitychange | pageshow         | focus     |
    // |-------------------------------------|-------------|------------------|------------------|-----------|
    // | 첫 진입                              | -           | X                | O                | O         |
    // | 가만히 화면 보고 있음 (1분 이상)       | O           | X                | X                | X         |
    // | 다른 탭 갔다가 돌아옴 (같은 윈도우)     | -           | O                | X                | O         |
    // | 브라우저 최소화 → 복원                 | -           | O                | X                | O         |
    // | 뒤로가기로 페이지 복귀 (bfcache)       | -           | X                | O (persisted)    | 브라우저 의존 |
    // | RN 앱 백그라운드 → 포그라운드          | -           | O                | 구현 의존         | 구현 의존  |
    // | 새로고침                             | -           | X                | O                | O         |

    // 1분마다 주기적으로 날짜 변경 체크 (가만히 두어도 결국 잡힘)
    const intervalId = setInterval(checkDateChange, 60_000);
    // 탭/WebView 가시성 변경 (다른 탭 갔다 돌아옴, 앱 백그라운드 → 포그라운드)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // bfcache 복귀 또는 WebView가 다시 화면에 표시될 때
    window.addEventListener('pageshow', checkDateChange);
    // 윈도우가 포커스를 받을 때 (다른 창에서 돌아옴)
    window.addEventListener('focus', checkDateChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', checkDateChange);
      window.removeEventListener('focus', checkDateChange);
    };
  }, [navigate]);
};
